import React, { FC, useCallback, useContext, useState } from 'react';

import { IBaseProps, SupportedLanguages } from '@rbi-ctg/frontend';
import { MenuObject } from '@rbi-ctg/menu';
import { MenuObjectTypes } from 'enums/menu';
import { useMenuVariant } from 'experiments/menu-variant/use-menu-variant';
import {
  Channel,
  IEntity,
  PosDataServiceMode,
  ServiceMode,
  useStoreMenuLazyQuery,
} from 'generated/graphql-gateway';
import {
  useGetComboWithVendorConfigLazyQuery,
  useGetItemWithVendorConfigLazyQuery,
  useGetMenuSectionsLazyQuery,
  useGetPickerWithVendorConfigLazyQuery,
} from 'generated/sanity-graphql';
import { useFeatureMenu } from 'hooks/use-feature-menu';
import { withLazySimpleCache } from 'state/graphql/with-simple-cache';
import { useLocale } from 'state/intl';
import { useServiceModeContext } from 'state/service-mode';
import { GraphQLErrorCodes, parseGraphQLErrorCodes } from 'utils/errors';
import logger from 'utils/logger';
import { useMemoAll } from 'utils/use-memo-all';

import { IMainMenuContext } from './types';
import { useDayPartSelector } from './use-day-part-selector';

export const MainMenuContext = React.createContext<IMainMenuContext>({
  data: undefined,
  loading: false,
  getStoreMenu: () => Promise.resolve(),
  getMenuObject: () => ({ data: {} as MenuObject, loading: false }),
  getMainMenu: () => {},
  getDayPart: () => ({ currentDayPart: null, dayParts: [] }),
  setDayPart: () => null,
  breakfastInterval: undefined,
  mainMenuInterval: undefined,
  getPricingAndAvailability: () => null,
  getCalories: () => null,
  getStoreMenuEntity: () => null,
  hasError: false,
  getSection: () => Promise.resolve(),
  getPickerDetail: () => Promise.resolve(),
  getItemDetail: () => Promise.resolve(),
  getComboDetail: () => Promise.resolve(),
  pickerLoading: false,
  pickerData: undefined,
  comboLoading: false,
  comboData: undefined,
  itemLoading: false,
  itemData: undefined,
  storeMenuLoading: false,
  storeMenuCalled: false,
  storeMenuData: undefined,
  setIsOffer: isOffer => isOffer,
  isOffer: false,
  isMenuLoading: false,
});

const matchSanityIdFromPath = (id: string) => {
  const [, sanityId] = id.split(/-(.*)/);

  if (!sanityId) {
    return '';
  }

  return sanityId;
};

const useCachedGetMenuSectionsLazyQuery = withLazySimpleCache(useGetMenuSectionsLazyQuery);
const useCachedStoreMenuLazyQuery = withLazySimpleCache(useStoreMenuLazyQuery);

export const MainMenuProvider: FC<React.PropsWithChildren<IBaseProps>> = ({ children }) => {
  const { setDayPart, getDayPart, breakfastInterval, mainMenuInterval } = useDayPartSelector();
  const { serviceMode } = useServiceModeContext();

  const { featureMenu } = useFeatureMenu();
  const { variantName } = useMenuVariant(featureMenu) || {};

  const { language } = useLocale();
  const prevLanguageRef = React.useRef<SupportedLanguages>(language);

  const [menuCompletePending, setMenuCompletePending] = useState<boolean>(false);
  const [storeMenuCompletePending, setStoreMenuCompletePending] = useState<boolean>(false);

  const [menuSectionMap, setMenuSectionMap] = useState<Map<string, any>>(new Map());
  const [getMenuSectionsLazy, { loading, error, data }] = useCachedGetMenuSectionsLazyQuery({
    onCompleted(data) {
      setMenuSectionMap(new Map(data.Menu?.options?.map(x => [(x as any)._id, x])));
      setMenuCompletePending(false);
    },
    onError() {
      setMenuCompletePending(false);
    },
  });
  const [
    getPicker,
    { loading: pickerLoading, error: pickerError, data: pickerData },
  ] = useGetPickerWithVendorConfigLazyQuery();
  const [
    getCombo,
    { loading: comboLoading, error: comboError, data: comboData },
  ] = useGetComboWithVendorConfigLazyQuery();
  const [
    getItem,
    { loading: itemLoading, error: itemError, data: itemData },
  ] = useGetItemWithVendorConfigLazyQuery();

  const [storeMenuDataMap, setStoreMenuDataMap] = useState<Map<string, IEntity>>(new Map());

  const [
    getStoreMenuLazy,
    {
      loading: storeMenuLoading,
      error: storeMenuError,
      called: storeMenuCalled,
      data: storeMenuData,
    },
  ] = useCachedStoreMenuLazyQuery({
    onCompleted(data) {
      setStoreMenuDataMap(new Map(data.storeMenu?.map(menu => [menu.id, menu])));
      setStoreMenuCompletePending(false);
    },
    onError() {
      setStoreMenuCompletePending(false);
    },
  });

  const isMenuLoading =
    loading || storeMenuLoading || menuCompletePending || storeMenuCompletePending;

  const posDataServiceMode =
    ServiceMode.DELIVERY === serviceMode ? PosDataServiceMode.DELIVERY : PosDataServiceMode.PICKUP;

  const getStoreMenu: IMainMenuContext['getStoreMenu'] = useCallback(
    async ({ region, storeId, forceRefresh }) => {
      const variables = {
        channel: Channel.WHITELABEL,
        region,
        storeId,
        serviceMode: posDataServiceMode,
      };

      const { error } = await getStoreMenuLazy({
        allowFromCache: !forceRefresh,
        variables: { ...variables, variantName },
      });

      // If fetching a variantName returned 404 we'll do a second attempt without variantName
      // Otherwise the user might have a blank menu due to a possible misconfiguration.
      const storeMenuNotFound = Boolean(
        error &&
          parseGraphQLErrorCodes(error).some(
            parsedError => parsedError.errorCode === GraphQLErrorCodes.STORE_MENU_NOT_FOUND
          )
      );
      if (variantName && storeMenuNotFound) {
        logger.warn(`Store Menu not found for variant menu: ${{ ...variables, variantName }}`);
        getStoreMenuLazy({
          allowFromCache: !forceRefresh,
          variables,
        });
      }
    },
    [getStoreMenuLazy, variantName, posDataServiceMode]
  );
  const [isOffer, setIsOffer] = useState(false);

  const getMainMenu: IMainMenuContext['getMainMenu'] = useCallback(
    ({ menuId, region, storeId, forceRefresh }) => {
      // Prevent getting menu sections from the cache when the language has changed.
      const languageChanged = prevLanguageRef.current !== language;
      const allowFromCache = !languageChanged;

      if (menuId && !storeId) {
        setMenuCompletePending(true);
        getMenuSectionsLazy({ allowFromCache, variables: { id: menuId } });
        prevLanguageRef.current = language;
      }

      if (menuId && storeId && region) {
        setMenuCompletePending(true);
        setStoreMenuCompletePending(true);
        // this should be awaited but it really screws up the types and it works anyways
        Promise.all([
          getStoreMenu({ region, storeId, forceRefresh }),
          getMenuSectionsLazy({ allowFromCache, variables: { id: menuId } }),
        ]);
        prevLanguageRef.current = language;
      }
    },
    [getMenuSectionsLazy, getStoreMenu, language]
  );
  const getPickerDetail = useCallback(
    async (id: string) => {
      const sanityId = matchSanityIdFromPath(id);

      await getPicker({ variables: { id: sanityId } });
    },
    [getPicker]
  );
  const getComboDetail = useCallback(
    async (id: string) => {
      const sanityId = matchSanityIdFromPath(id);

      await getCombo({ variables: { id: sanityId } });
    },
    [getCombo]
  );
  const getItemDetail = useCallback(
    async (id: string) => {
      const sanityId = matchSanityIdFromPath(id);

      await getItem({ variables: { id: sanityId } });
    },
    [getItem]
  );

  const getCachedSection = useCallback(
    (id: string) => {
      return menuSectionMap.get(id.replace('Section:', ''));
    },
    [menuSectionMap]
  );

  const getPricingAndAvailability = useCallback(
    (id: string) => {
      if (isMenuLoading || !storeMenuData) {
        return null;
      }

      return storeMenuDataMap.get(id);
    },
    [isMenuLoading, storeMenuData, storeMenuDataMap]
  );

  const getCalories = useCallback(
    (item: MenuObject) => {
      if (!item || !item._id) {
        return null;
      }

      return storeMenuDataMap.get(item._id)?.calories || null;
    },
    [storeMenuDataMap]
  );

  const getStoreMenuEntity = useCallback(
    (menuEntityId: string) => {
      if (storeMenuLoading || !storeMenuData) {
        return null;
      }

      return storeMenuDataMap.get(menuEntityId);
    },
    [storeMenuData, storeMenuDataMap, storeMenuLoading]
  );

  // TODO - the switch here is likely legacy and can be removed
  const getMenuObject = useCallback(
    (id: string) => {
      const [type, sanityId] = id.split(/-(.*)/);

      switch (type) {
        case MenuObjectTypes.SECTION:
          return { loading: false, data: getCachedSection(`Section:${sanityId}`) as MenuObject };
        default:
          logger.error(`${id} is unhandled by getMenuObject - may cause a user facing issue`);
          return { loading: false, data: {} as MenuObject };
      }
    },
    [getCachedSection]
  );

  const hasError = Boolean(error || pickerError || comboError || itemError || storeMenuError);

  const value = useMemoAll({
    data,
    getStoreMenu,
    getMainMenu,
    getMenuObject,
    getPickerDetail,
    getComboDetail,
    getItemDetail,
    getSection: getCachedSection,
    hasError,
    loading,
    isMenuLoading,
    pickerLoading,
    pickerData,
    comboLoading,
    comboData,
    itemLoading,
    itemData,
    storeMenuCalled,
    storeMenuLoading,
    storeMenuData,
    setDayPart,
    getDayPart,
    breakfastInterval,
    mainMenuInterval,
    getPricingAndAvailability,
    getCalories,
    getStoreMenuEntity,
    isOffer,
    setIsOffer,
  });

  return <MainMenuContext.Provider value={value}>{children}</MainMenuContext.Provider>;
};

// MainMenuContext.Provider.whyDidYouRender = true;

export const useMainMenuContext = () => useContext(MainMenuContext);
export default MainMenuContext.Consumer;
